import { useEffect, useRef } from 'react';
import {
  widget,
  ChartingLibraryWidgetOptions,
  IChartingLibraryWidget,
  IBasicDataFeed,
  IDatafeedChartApi,
  IExternalDatafeed,
  IDatafeedQuotesApi,
} from 'charting_library';
import * as React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ColorMode } from 'theme';

const LIBRARY_PATH = '/js/charting_library/charting_library/';

export interface Props {
  initializingChartContainerProps: Partial<ChartingLibraryWidgetOptions>;
  onChartReady?: (chart: IChartingLibraryWidget) => void;
  onHeaderReady?: (chart: IChartingLibraryWidget) => void;
  datafeed:
    | IBasicDataFeed
    | (IDatafeedChartApi & IExternalDatafeed & IDatafeedQuotesApi);
  destructor?: () => void;
}

export const ChartingLibrary = ({
  initializingChartContainerProps,
  onChartReady,
  onHeaderReady,
  datafeed,
  destructor,
}: Props) => {
  const chartContainerRef =
    useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;

  const tradingViewWidget = useRef<
    (IChartingLibraryWidget & { removed?: boolean }) | undefined
  >();
  const theme = useTheme();

  useEffect(
    () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises, @typescript-eslint/require-await
      (async () => {
        tradingViewWidget.current?.remove();

        const widgetOptions = {
          datafeed,

          container: chartContainerRef.current,
          library_path: LIBRARY_PATH,
          locale: 'en',
          client_id: 'tradingview.com',
          user_id: 'public_user_id',
          enabled_features: ['study_templates'],
          fullscreen: false,
          autosize: true,
          timezone: 'America/New_York',
          theme: theme.colorMode == ColorMode.LIGHT ? 'light' : 'dark',
          custom_css_url: '/css/charting_library_custom.css',

          ...initializingChartContainerProps,
        } as ChartingLibraryWidgetOptions;

        const tvWidget = new widget(widgetOptions);
        tradingViewWidget.current = tvWidget;

        tvWidget.onChartReady(() => {
          if (onChartReady != null) {
            onChartReady(tvWidget);
          }
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          tvWidget.headerReady().then(() => {
            if (onHeaderReady != null) {
              onHeaderReady(tvWidget);
            }
          });
        });
      })();

      return () => {
        if (tradingViewWidget.current != null) {
          tradingViewWidget.current.remove();
          tradingViewWidget.current.removed = true;
        }
        tradingViewWidget.current = undefined;
        destructor?.();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initializingChartContainerProps, datafeed, theme.colorMode],
  );

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: '40vh',
        width: '100%',
        flexGrow: 1,
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: '100%',
        }}
        ref={chartContainerRef}
        className={'ChartingLibraryContainer'}
      />
    </Box>
  );
};
